import { type BaseWidget, WidgetEvent, type WidgetEventCallback } from '@examdojo/core/widget';
import type { WidgetConfig, WidgetState } from '@examdojo/core/widget';
import type { ActionDefinition, ActionResult } from '@examdojo/core/widget';
import katex from 'katex';

import { compileExpression } from '../function-test/expression.compile';
import {
  deriveTransformInverse,
  type FunctionMachineBlock,
  type FunctionMachineFunctionBlock,
  type FunctionMachineTransformBlock,
  type FunctionMachineValueBlock,
  type RunningState,
} from './function-machine.model';
import { STYLES } from './function-machine.styles';

// ---------------------------------------------------------------------------
// Config type for the factory system
// ---------------------------------------------------------------------------

export interface FunctionMachineWidgetConfig extends WidgetConfig<'function-machine', FunctionMachineWidgetParams> {
  display: FunctionMachineWidgetDisplay;
}

interface FunctionMachineWidgetParams {
  blocks: FunctionMachineBlock[];
  inverse?: boolean;
}

interface FunctionMachineWidgetDisplay {
  layout?: 'horizontal' | 'vertical';
  showRunButton?: boolean;
  runButtonMode?: 'global' | 'per-block';
  compact?: boolean;
  precision?: number;
}

// ---------------------------------------------------------------------------
// Gear SVG path (FontAwesome gear icon)
// ---------------------------------------------------------------------------

const GEAR_SVG = `<svg width="16" height="16" viewBox="0 0 512 512" fill="currentColor"><path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"/></svg>`;

const PLAY_SVG = `<svg width="10" height="10" viewBox="0 0 384 512" fill="currentColor"><path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"/></svg>`;

const ARROW_SVG = `<svg class="fm-arrow__svg" viewBox="0 0 40 24"><line x1="0" y1="12" x2="30" y2="12" stroke="currentColor" stroke-width="2"/><polygon points="30,6 40,12 30,18" fill="currentColor"/></svg>`;

const REVERSE_SVG = `<svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M32 96l320 0V32c0-12.9 7.8-24.6 19.8-29.6s25.7-2.2 34.9 6.9l96 96c6 6 9.4 14.1 9.4 22.6s-3.4 16.6-9.4 22.6l-96 96c-9.2 9.2-22.9 11.9-34.9 6.9s-19.8-16.6-19.8-29.6V160L32 160c-17.7 0-32-14.3-32-32s14.3-32 32-32zM480 352c17.7 0 32 14.3 32 32s-14.3 32-32 32l-320 0v64c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-96-96c-6-6-9.4-14.1-9.4-22.6s3.4-16.6 9.4-22.6l96-96c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6v64l320 0z"/></svg>`;

// ---------------------------------------------------------------------------
// Widget class
// ---------------------------------------------------------------------------

/**
 * DOM-based function machine widget. Renders pure HTML/CSS into the container.
 * Registered in the factory alongside JSXGraph widgets but doesn't use JSXGraph.
 */
export class FunctionMachineWidget implements BaseWidget<FunctionMachineWidgetConfig> {
  constructor(container: HTMLElement) {
    this.container = container;
  }

  readonly id: string = '';
  readonly type = 'function-machine' as const;

  private readonly container: HTMLElement;
  private config!: FunctionMachineWidgetConfig;
  private readonly emitter = new SimpleEventEmitter();

  // State
  private readonly inputValues = new Map<string, number>();
  private readonly computedValues = new Map<string, number | null>();
  private readonly errors = new Map<string, string>();
  private runningState: RunningState = 'idle';
  private animatedBlockIndex = -1;
  private readonly compiledExpressions = new Map<string, (v: number) => number>();
  private isInverseActive = false;
  private hasInverse = false;

  // DOM references for updates
  private readonly valueElements = new Map<string, HTMLElement>();
  private arrowElements: HTMLElement[] = [];
  private readonly gearElements = new Map<string, HTMLElement>();
  private readonly valueBlockContainers = new Map<string, HTMLElement>();
  private readonly functionBlockElements = new Map<string, HTMLElement>();
  private revertGeneration = 0;
  private rootElement: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;

  init(config: FunctionMachineWidgetConfig): void {
    this.config = config;
    (this as { id: string }).id = config.id;
    this.hasInverse = this.detectHasInverse();
    this.initInputValues();
    this.compileExpressions();
    this.render();
    this.setAutoSize();
    this.emitter.emit(WidgetEvent.Ready, {});
  }

  destroy(): void {
    this.emitter.clear();
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
    this.container.innerHTML = '';
    this.resetAutoSize();
    this.valueElements.clear();
    this.arrowElements = [];
    this.gearElements.clear();
    this.valueBlockContainers.clear();
    this.functionBlockElements.clear();
  }

  update(config: Partial<FunctionMachineWidgetConfig>): void {
    if (config.params) {
      this.config = { ...this.config, ...config } as FunctionMachineWidgetConfig;
      this.initInputValues();
      this.compileExpressions();
      this.render();
    }
  }

  async executeAction(_action: ActionDefinition): Promise<ActionResult> {
    return { success: true };
  }

  getState(): WidgetState {
    const values: Record<string, number> = {};
    for (const [k, v] of this.computedValues) {
      if (v !== null) {
        values[k] = v;
      }
    }
    return { type: this.type, id: this.id, inputValues: Object.fromEntries(this.inputValues), computedValues: values };
  }

  setState(_state: WidgetState): void {
    // No-op for now
  }

  on(event: string, cb: WidgetEventCallback): void {
    this.emitter.on(event, cb);
  }

  off(event: string, cb: WidgetEventCallback): void {
    this.emitter.off(event, cb);
  }

  // ---------------------------------------------------------------------------
  // Auto-sizing
  // ---------------------------------------------------------------------------

  /**
   * Override the h-full / flex-1 chain so this DOM widget sizes itself.
   * Walks up from widget-content → widget-container, converting each layer to auto-height.
   */
  private setAutoSize(): void {
    // widget-content (our direct container)
    this.container.style.height = 'auto';

    // widget-canvas
    const canvas = this.container.closest('.widget-canvas') as HTMLElement | null;
    if (canvas) {
      canvas.style.flex = 'none';
      canvas.style.height = 'auto';
    }

    // widget-main
    const main = this.container.closest('.widget-main') as HTMLElement | null;
    if (main) {
      main.style.flex = 'none';
      main.style.height = 'auto';
    }

    // widget-body
    const body = this.container.closest('.widget-body') as HTMLElement | null;
    if (body) {
      body.style.flex = 'none';
      body.style.height = 'auto';
    }

    // widget-wrapper — also remove border
    const wrapper = this.container.closest('.widget-wrapper') as HTMLElement | null;
    if (wrapper) {
      wrapper.style.height = 'auto';
      wrapper.style.border = 'none';
    }

    // dojo-widget host element
    const dojoWidget = wrapper?.parentElement;
    if (dojoWidget?.tagName === 'DOJO-WIDGET') {
      dojoWidget.style.height = 'auto';
    }

    // widget-container (TipTap editor wrapper)
    const widgetContainer = dojoWidget?.closest('.widget-container') as HTMLElement | null;
    if (widgetContainer) {
      widgetContainer.style.height = 'auto';
    }

    // Published-view widget-wrapper (renderer)
    const rendererWrapper = dojoWidget?.closest('.widget-wrapper') as HTMLElement | null;
    if (rendererWrapper && rendererWrapper !== wrapper) {
      rendererWrapper.style.height = 'auto';
    }
  }

  /**
   * Undo the inline style overrides applied by setAutoSize so the next widget
   * (e.g. a JSXGraph widget reusing the same container) gets the original flex layout back.
   */
  private resetAutoSize(): void {
    this.container.style.height = '';

    const canvas = this.container.closest('.widget-canvas') as HTMLElement | null;
    if (canvas) {
      canvas.style.flex = '';
      canvas.style.height = '';
    }

    const main = this.container.closest('.widget-main') as HTMLElement | null;
    if (main) {
      main.style.flex = '';
      main.style.height = '';
    }

    const body = this.container.closest('.widget-body') as HTMLElement | null;
    if (body) {
      body.style.flex = '';
      body.style.height = '';
    }

    const wrapper = this.container.closest('.widget-wrapper') as HTMLElement | null;
    if (wrapper) {
      wrapper.style.height = '';
      wrapper.style.border = '';
    }

    const dojoWidget = wrapper?.parentElement;
    if (dojoWidget?.tagName === 'DOJO-WIDGET') {
      dojoWidget.style.height = '';
    }

    const widgetContainer = dojoWidget?.closest('.widget-container') as HTMLElement | null;
    if (widgetContainer) {
      widgetContainer.style.height = '';
    }

    const rendererWrapper = dojoWidget?.closest('.widget-wrapper') as HTMLElement | null;
    if (rendererWrapper && rendererWrapper !== wrapper) {
      rendererWrapper.style.height = '';
    }
  }

  // ---------------------------------------------------------------------------
  // Inverse mode helpers
  // ---------------------------------------------------------------------------

  private detectHasInverse(): boolean {
    if (this.config.params.inverse !== undefined) {
      return true;
    }
    return this.config.params.blocks.some((b) => {
      if (b.type === 'value') {
        return b.inverseLabel !== undefined || b.inverseEditable !== undefined;
      }
      if (b.type === 'function') {
        return b.inverseExpression !== undefined;
      }
      if (b.type === 'transform') {
        return b.inverseExpression !== undefined || b.inverseOperation !== undefined;
      }
      return false;
    });
  }

  private getEffectiveBlocks(): FunctionMachineBlock[] {
    const blocks = this.config.params.blocks;
    if (!this.isInverseActive) {
      return blocks;
    }
    return blocks.map((b) => {
      switch (b.type) {
        case 'value':
          return {
            ...b,
            value: b.inverseValue ?? b.value,
            label: b.inverseLabel ?? b.label,
            editable: b.inverseEditable ?? b.editable,
          };
        case 'function':
          return {
            ...b,
            expression: b.inverseExpression ?? b.expression,
            name: b.inverseName ?? b.name,
            color: b.inverseColor ?? b.color,
          };
        case 'transform': {
          const auto = deriveTransformInverse(b.expression, b.variable);
          return {
            ...b,
            expression: b.inverseExpression ?? auto?.expression ?? b.expression,
            operation: b.inverseOperation ?? auto?.operation ?? b.operation,
            color: b.inverseColor ?? b.color,
          };
        }
        default:
          return b;
      }
    });
  }

  /** True when inverse mode is active (arrows flip, expressions swap). */
  private isInverseState(): boolean {
    return this.isInverseActive;
  }

  // ---------------------------------------------------------------------------
  // Initialization helpers
  // ---------------------------------------------------------------------------

  private initInputValues(): void {
    this.inputValues.clear();
    this.computedValues.clear();
    this.errors.clear();
    this.animatedBlockIndex = -1;
    this.runningState = 'idle';
    this.isInverseActive = this.config.params.inverse ?? false;

    // Use effective blocks so inverseValue is resolved when starting in inverse mode
    for (const block of this.getEffectiveBlocks()) {
      if (block.type === 'value' && block.value !== undefined) {
        this.inputValues.set(block.id, block.value);
      }
    }
  }

  private compileExpressions(): void {
    this.compiledExpressions.clear();
    for (const block of this.getEffectiveBlocks()) {
      if (block.type === 'function' || block.type === 'transform') {
        try {
          this.compiledExpressions.set(block.id, compileExpression(block.expression, block.variable ?? 'x'));
        } catch {
          // Will error at evaluation time
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  private render(): void {
    this.container.innerHTML = '';
    this.valueElements.clear();
    this.arrowElements = [];
    this.gearElements.clear();
    this.valueBlockContainers.clear();
    this.functionBlockElements.clear();

    // Inject styles (re-create after innerHTML clear)
    this.styleElement = document.createElement('style');
    this.styleElement.textContent = STYLES;
    this.container.appendChild(this.styleElement);

    const display = this.config.display;
    const layout = display?.layout ?? 'horizontal';
    const showRunButton = display?.showRunButton !== false;
    const runButtonMode = display?.runButtonMode ?? 'global';
    const visibleBlocks = this.getVisibleBlocks();

    // Root container
    const root = document.createElement('div');
    root.className = 'fm';
    root.classList.add(`fm--${layout}`);
    if (this.isInverseState()) {
      root.classList.add('fm--inverse');
    }
    root.setAttribute('role', 'figure');
    root.setAttribute('aria-label', `Function machine with ${visibleBlocks.length} steps`);
    this.rootElement = root;

    for (let i = 0; i < visibleBlocks.length; i++) {
      const { block, isFirst, isLast } = visibleBlocks[i];

      // Arrow before block (except first)
      if (i > 0) {
        const arrow = document.createElement('div');
        arrow.className = 'fm-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.innerHTML = ARROW_SVG;
        root.appendChild(arrow);
        this.arrowElements.push(arrow);
      }

      // Block
      switch (block.type) {
        case 'value':
          root.appendChild(this.createValueBlock(block, isFirst, isLast));
          break;
        case 'function':
          root.appendChild(this.createFunctionBlock(block, showRunButton && runButtonMode === 'per-block', i));
          break;
        case 'transform':
          root.appendChild(this.createTransformBlock(block, showRunButton && runButtonMode === 'per-block', i));
          break;
      }
    }

    this.container.appendChild(root);

    // Controls: Invert toggle + Run button, or single global run button
    if (showRunButton && runButtonMode === 'global') {
      if (this.hasInverse) {
        const wrapper = document.createElement('div');
        wrapper.className = 'fm-controls';

        // Invert toggle
        const invColor = this.getInverseColor();
        const invertBtn = document.createElement('button');
        invertBtn.className = 'fm-invert-btn';
        if (this.isInverseActive) {
          invertBtn.classList.add('fm-invert-btn--active');
        }
        invertBtn.style.setProperty('--invert-color', `var(--${invColor}-500)`);
        invertBtn.style.setProperty('--invert-hover', `var(--${invColor}-600)`);
        invertBtn.style.setProperty('--invert-bg', `var(--${invColor}-50)`);
        invertBtn.innerHTML = `${REVERSE_SVG} Inverse`;
        invertBtn.setAttribute('aria-label', 'Toggle inverse mode');
        invertBtn.addEventListener('click', () => this.onToggleInverse());
        wrapper.appendChild(invertBtn);

        // Run button
        const primaryColor = this.getPrimaryColor();
        const runBtn = this.createRunButton('fm-run-btn--global');
        runBtn.style.setProperty('--btn-color', `var(--${primaryColor}-500)`);
        runBtn.style.setProperty('--btn-hover', `var(--${primaryColor}-600)`);
        runBtn.addEventListener('click', () => this.onRunAll());
        wrapper.appendChild(runBtn);

        this.container.appendChild(wrapper);
      } else {
        const wrapper = document.createElement('div');
        wrapper.className = 'fm-global-run';
        const primaryColor = this.getPrimaryColor();
        const btn = this.createRunButton('fm-run-btn--global');
        btn.style.setProperty('--btn-color', `var(--${primaryColor}-500)`);
        btn.style.setProperty('--btn-hover', `var(--${primaryColor}-600)`);
        btn.addEventListener('click', () => this.onRunAll());
        wrapper.appendChild(btn);
        this.container.appendChild(wrapper);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Block creation
  // ---------------------------------------------------------------------------

  private createValueBlock(block: FunctionMachineValueBlock, isFirst: boolean, isLast: boolean): HTMLElement {
    const el = document.createElement('div');
    el.className = 'fm-block fm-block--value';
    this.valueBlockContainers.set(block.id, el);

    const inv = this.isInverseActive;
    const label = block.label ?? (isFirst ? (inv ? 'Output' : 'Input') : isLast ? (inv ? 'Input' : 'Output') : null);
    const shouldShowLabel = block.showLabel !== undefined ? block.showLabel : isFirst || isLast;
    if (label && shouldShowLabel) {
      const labelEl = document.createElement('span');
      labelEl.className = 'fm-block__label';
      labelEl.textContent = label;
      el.appendChild(labelEl);
    }

    const editable = this.isEditable(block);
    if (editable) {
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'fm-block__input';
      const val = this.inputValues.get(block.id);
      if (val !== undefined) {
        input.value = this.formatNumber(val);
      } else if (block.value !== undefined) {
        input.value = this.formatNumber(block.value);
      }
      input.setAttribute('aria-label', `Enter ${label ?? 'value'}`);
      input.addEventListener('input', () => {
        const v = parseFloat(input.value);
        if (!Number.isNaN(v)) {
          this.inputValues.set(block.id, v);
          this.emitter.emit(WidgetEvent.UserInteraction, { blockId: block.id, value: v });
        }
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.onRunAll();
        }
      });
      el.appendChild(input);
    } else {
      const span = document.createElement('span');
      span.className = 'fm-block__value fm-block__value--pending';
      span.textContent = '?';
      el.appendChild(span);
      this.valueElements.set(block.id, span);
    }

    return el;
  }

  private createFunctionBlock(block: FunctionMachineFunctionBlock, showRun: boolean, visIdx: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'fm-block-wrapper';

    const el = document.createElement('div');
    el.className = 'fm-block fm-block--function';
    // Neutral by default; highlight CSS vars used during animation
    const bgShade = block.color === 'neutral' ? '50' : '100';
    el.style.setProperty('--block-highlight-bg', `var(--${block.color}-${bgShade})`);
    el.style.setProperty('--block-highlight-text', `var(--${block.color}-600)`);
    this.functionBlockElements.set(block.id, el);

    // Gear — always uses the block's accent color
    const gear = document.createElement('div');
    gear.className = 'fm-block__gear';
    gear.style.color = `var(--${block.color}-500)`;
    gear.innerHTML = GEAR_SVG;
    el.appendChild(gear);
    this.gearElements.set(block.id, gear);

    // Expression
    const exprEl = document.createElement('span');
    exprEl.className = 'fm-block__expression';
    this.renderKatex(exprEl, block);
    el.appendChild(exprEl);

    wrapper.appendChild(el);

    if (showRun) {
      const btn = this.createRunButton();
      btn.addEventListener('click', () => this.onRunUpTo(visIdx));
      wrapper.appendChild(btn);
    }

    return wrapper;
  }

  private createTransformBlock(block: FunctionMachineTransformBlock, showRun: boolean, visIdx: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'fm-block-wrapper';

    const el = document.createElement('div');
    el.className = 'fm-block fm-block--transform';

    // Filled/dominant variant when the block has a color
    if (block.color) {
      el.classList.add('fm-block--transform--filled');
      el.style.setProperty('--transform-fill', `var(--${block.color}-500)`);
    }

    // Set highlight CSS vars for animation
    const colorName = this.getTransformColorName(block);
    el.style.setProperty('--block-highlight-bg', `var(--${colorName}-100)`);
    el.style.setProperty('--block-highlight-text', `var(--${colorName}-600)`);
    this.functionBlockElements.set(block.id, el);

    const span = document.createElement('span');
    span.className = 'fm-block__operation';
    this.renderOperationKatex(span, block);
    el.appendChild(span);

    wrapper.appendChild(el);

    if (showRun) {
      const btn = this.createRunButton();
      btn.addEventListener('click', () => this.onRunUpTo(visIdx));
      wrapper.appendChild(btn);
    }

    return wrapper;
  }

  private createRunButton(extraClass?: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = extraClass ? `fm-run-btn ${extraClass}` : 'fm-run-btn';
    btn.innerHTML = `${PLAY_SVG} Run`;
    btn.setAttribute('aria-label', 'Run function machine');
    return btn;
  }

  /** Remove visual highlights (borders, bg, arrows) without clearing computed values. */
  private revertHighlights(): void {
    for (const [, el] of this.valueBlockContainers) {
      el.classList.remove('fm-block--value--highlighted');
    }
    for (const [, el] of this.functionBlockElements) {
      el.classList.remove('fm-block--function--highlighted');
      el.classList.remove('fm-block--transform--highlighted');
    }
    for (const arrow of this.arrowElements) {
      arrow.classList.remove('fm-arrow--animated');
      arrow.style.removeProperty('--arrow-color');
    }
  }

  // ---------------------------------------------------------------------------
  // KaTeX rendering
  // ---------------------------------------------------------------------------

  private renderKatex(el: HTMLElement, block: FunctionMachineFunctionBlock): void {
    const variable = block.variable ?? 'x';
    const katexExpr = this.toKatex(block.expression);
    const latex = block.name ? `${block.name}(${variable}) = ${katexExpr}` : katexExpr;

    try {
      katex.render(latex, el, { throwOnError: false, output: 'html' });
    } catch {
      el.textContent = block.expression;
    }
  }

  private renderOperationKatex(el: HTMLElement, block: FunctionMachineTransformBlock): void {
    const op = block.operation.replace(/×/g, '\\times ').replace(/÷/g, '\\div ').replace(/−/g, '-');
    try {
      katex.render(op, el, { throwOnError: false, output: 'html' });
    } catch {
      el.textContent = block.operation;
    }
  }

  // ---------------------------------------------------------------------------
  // Evaluation
  // ---------------------------------------------------------------------------

  private getForwardColorName(): string {
    for (const b of this.config.params.blocks) {
      if (b.type === 'function') {
        return b.color;
      }
      if (b.type === 'transform' && b.color) {
        return b.color;
      }
    }
    return 'sky';
  }

  private getInverseColorName(): string {
    for (const b of this.config.params.blocks) {
      if (b.type === 'function' && b.inverseColor) {
        return b.inverseColor;
      }
      if (b.type === 'transform' && b.inverseColor) {
        return b.inverseColor;
      }
    }
    return 'pink';
  }

  private getThemeColorName(): string {
    return this.isInverseActive ? this.getInverseColorName() : this.getForwardColorName();
  }

  private getPrimaryColor(): string {
    return this.getForwardColorName();
  }

  private getInverseColor(): string {
    return this.getInverseColorName();
  }

  private onToggleInverse(): void {
    if (this.runningState === 'running') {
      return;
    }

    // Carry over computed values as input for the new mode's editable block
    const prevComputed = new Map(this.computedValues);
    this.isInverseActive = !this.isInverseActive;
    this.compileExpressions();

    // Find the block that will be editable in the new mode and set its input
    for (const block of this.getEffectiveBlocks()) {
      if (block.type === 'value' && this.isEditable(block)) {
        const val = prevComputed.get(block.id);
        if (val !== null && val !== undefined) {
          this.inputValues.set(block.id, val);
        }
      }
    }

    // Reset evaluation state (no auto-run — user clicks Run separately)
    this.computedValues.clear();
    this.errors.clear();
    this.animatedBlockIndex = -1;

    this.render();
    this.setAutoSize();
  }

  private onRunAll(): void {
    if (this.runningState === 'running') {
      return;
    }
    void this.runEvaluation(this.getEffectiveBlocks().length - 1);
  }

  private onRunUpTo(visIdx: number): void {
    if (this.runningState === 'running') {
      return;
    }
    const visible = this.getVisibleBlocks();
    const block = visible[visIdx];
    if (!block) {
      return;
    }
    const fullIdx = this.getEffectiveBlocks().findIndex((b) => b.id === block.block.id);
    if (fullIdx >= 0) {
      void this.runEvaluation(fullIdx);
    }
  }

  private async runEvaluation(upToIndex: number): Promise<void> {
    const allBlocks = this.getEffectiveBlocks();
    const isReverse = this.isInverseActive;
    const visible = this.getVisibleBlocks();
    const totalSteps = visible.length + (visible.length - 1);
    const duration = 600;
    const stepDelay = duration / Math.max(totalSteps, 1);
    const generation = ++this.revertGeneration;

    this.runningState = 'running';
    this.animatedBlockIndex = -1;
    this.errors.clear();
    this.computedValues.clear();

    // Reset previous state
    this.revertHighlights();
    for (const [, el] of this.valueElements) {
      el.textContent = '?';
      el.classList.add('fm-block__value--pending');
      el.classList.remove('fm-block__value--error');
    }

    // Phase 1: Evaluate all values (no animation yet)
    let currentValue = this.getFirstInputValue();
    const results = new Map<string, number | null>();
    let hitError = false;
    let errorBlockId: string | null = null;

    if (isReverse) {
      for (let i = allBlocks.length - 1; i >= 0; i--) {
        const block = allBlocks[i];
        if (block.type === 'function' || block.type === 'transform') {
          if (currentValue === null) {
            hitError = true;
            break;
          }
          const result = this.evaluateBlock(block.id, currentValue);
          if (result === null) {
            hitError = true;
            errorBlockId = block.id;
            break;
          }
          currentValue = result;
        }
        if (block.type === 'value' && !this.isEditable(block)) {
          results.set(block.id, currentValue);
        }
      }
    } else {
      for (let i = 0; i <= upToIndex && i < allBlocks.length; i++) {
        const block = allBlocks[i];
        if (block.type === 'function' || block.type === 'transform') {
          if (currentValue === null) {
            hitError = true;
            break;
          }
          const result = this.evaluateBlock(block.id, currentValue);
          if (result === null) {
            hitError = true;
            errorBlockId = block.id;
            break;
          }
          currentValue = result;
        }
        if (block.type === 'value' && !this.isEditable(block)) {
          results.set(block.id, currentValue);
        }
      }
    }

    // Phase 2: Animate cascade
    const animateBlock = async (vi: number) => {
      const { block } = visible[vi];
      if (block.type === 'value') {
        const container = this.valueBlockContainers.get(block.id);
        if (container) {
          container.classList.add('fm-block--value--highlighted');
          container.style.setProperty('--highlight-color', this.getActiveColor());
        }
        if (!this.isEditable(block) && results.has(block.id)) {
          const val = results.get(block.id);
          const el = this.valueElements.get(block.id);
          if (el && val !== null && val !== undefined) {
            el.textContent = this.formatNumber(val);
            el.classList.remove('fm-block__value--pending');
            this.computedValues.set(block.id, val);
          }
        }
      } else if (block.type === 'function') {
        const funcEl = this.functionBlockElements.get(block.id);
        funcEl?.classList.add('fm-block--function--highlighted');
        const gear = this.gearElements.get(block.id);
        gear?.classList.add('fm-block__gear--spinning');
      } else if (block.type === 'transform') {
        const transformEl = this.functionBlockElements.get(block.id);
        transformEl?.classList.add('fm-block--transform--highlighted');
      }
    };

    const showError = (vi: number): boolean => {
      const { block } = visible[vi];
      if (hitError && errorBlockId === block.id) {
        const fullIdx = allBlocks.findIndex((b) => b.id === block.id);
        const targetVal = isReverse
          ? allBlocks
              .slice(0, fullIdx)
              .reverse()
              .find((b) => b.type === 'value')
          : allBlocks.slice(fullIdx + 1).find((b) => b.type === 'value');
        if (targetVal) {
          const el = this.valueElements.get(targetVal.id);
          if (el) {
            el.textContent = this.errors.get(block.id) ?? 'Error';
            el.classList.add('fm-block__value--error');
            el.classList.remove('fm-block__value--pending');
          }
        }
        return true;
      }
      return false;
    };

    if (isReverse) {
      for (let vi = visible.length - 1; vi >= 0; vi--) {
        if (vi < visible.length - 1) {
          const arrowIdx = vi;
          if (arrowIdx < this.arrowElements.length) {
            const arrow = this.arrowElements[arrowIdx];
            arrow.classList.add('fm-arrow--animated');
            arrow.style.setProperty('--arrow-color', this.getActiveColor());
          }
          await delay(stepDelay);
        }
        await animateBlock(vi);
        if (showError(vi)) {
          break;
        }
        await delay(stepDelay);
      }
    } else {
      for (let vi = 0; vi < visible.length; vi++) {
        const fullIdx = allBlocks.findIndex((b) => b.id === visible[vi].block.id);
        if (fullIdx > upToIndex) {
          break;
        }
        if (vi > 0) {
          const arrowIdx = vi - 1;
          if (arrowIdx < this.arrowElements.length) {
            const arrow = this.arrowElements[arrowIdx];
            arrow.classList.add('fm-arrow--animated');
            arrow.style.setProperty('--arrow-color', this.getActiveColor());
          }
          await delay(stepDelay);
        }
        await animateBlock(vi);
        if (showError(vi)) {
          break;
        }
        await delay(stepDelay);
      }
    }

    // Stop gear spinning
    for (const [, gear] of this.gearElements) {
      gear.classList.remove('fm-block__gear--spinning');
    }

    this.runningState = hitError ? 'idle' : 'complete';

    if (!hitError) {
      this.emitter.emit(WidgetEvent.StateChange, this.getState());
      await delay(1500);
      if (this.revertGeneration === generation) {
        this.revertHighlights();
      }
    } else {
      this.revertHighlights();
    }
  }

  private evaluateBlock(blockId: string, inputValue: number): number | null {
    const fn = this.compiledExpressions.get(blockId);
    if (!fn) {
      this.errors.set(blockId, 'Invalid');
      return null;
    }
    try {
      const result = fn(inputValue);
      if (!Number.isFinite(result)) {
        this.errors.set(blockId, 'Undefined');
        return null;
      }
      return result;
    } catch {
      this.errors.set(blockId, 'Error');
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private getActiveColor(): string {
    return `var(--${this.getThemeColorName()}-500)`;
  }

  private getTransformColorName(block: FunctionMachineTransformBlock): string {
    return block.color ?? this.getThemeColorName();
  }

  private getVisibleBlocks(): Array<{ block: FunctionMachineBlock; isFirst: boolean; isLast: boolean }> {
    const all = this.getEffectiveBlocks();
    const compact = this.config.display?.compact ?? false;

    let blocks: FunctionMachineBlock[];
    if (compact) {
      blocks = all.filter((b, i) => {
        if (b.type !== 'value') {
          return true;
        }
        return all.findIndex((bb) => bb.type === 'value') === i || all.findLastIndex((bb) => bb.type === 'value') === i;
      });
    } else {
      blocks = all;
    }

    return blocks.map((block, i) => ({
      block,
      isFirst: i === 0,
      isLast: i === blocks.length - 1,
    }));
  }

  private isEditable(block: FunctionMachineBlock): boolean {
    if (block.type !== 'value') {
      return false;
    }
    if (block.editable !== undefined) {
      return block.editable;
    }
    const blocks = this.getEffectiveBlocks();
    if (this.isInverseActive) {
      const lastValue = [...blocks].reverse().find((b) => b.type === 'value');
      return lastValue?.id === block.id;
    }
    const firstValue = blocks.find((b) => b.type === 'value');
    return firstValue?.id === block.id;
  }

  private getFirstInputValue(): number | null {
    for (const block of this.getEffectiveBlocks()) {
      if (block.type === 'value' && this.isEditable(block)) {
        return this.inputValues.get(block.id) ?? block.value ?? null;
      }
    }
    return null;
  }

  private formatNumber(value: number): string {
    const prec = this.config.display?.precision ?? 4;
    if (Number.isInteger(value)) {
      return String(value);
    }
    return parseFloat(value.toFixed(prec)).toString();
  }

  private toKatex(expr: string): string {
    return expr.replace(/\s*\*\s*/g, ' \\cdot ').replace(/\*\*(\d+)/g, '^{$1}');
  }
}

// ---------------------------------------------------------------------------
// Simple event emitter (standalone, no JSXGraph dependency)
// ---------------------------------------------------------------------------

class SimpleEventEmitter {
  private readonly handlers = new Map<string, Set<WidgetEventCallback>>();

  on(event: string, cb: WidgetEventCallback): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(cb);
  }

  off(event: string, cb: WidgetEventCallback): void {
    this.handlers.get(event)?.delete(cb);
  }

  emit(event: string, data: unknown): void {
    this.handlers.get(event)?.forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error(`Error in event handler for '${event}':`, e);
      }
    });
  }

  clear(): void {
    this.handlers.clear();
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
